import * as React from 'react';
import {
  useMemo,
  memo,
  useCallback,
  createContext,
  useContext,
  useState,
} from 'react';
import { ProFormDependency } from '@bedrock/eed-react';
import { ProFormInstance } from '@bedrock/eed-react/lib/components/ProForm/types';
import { useMemoizedFn } from 'ahooks';
import { isEqual, get } from 'lodash-es';

// 创建一个类，产出字段字节模型组件，每次添加会在模型中添加字段模型，添加字段模型会自动产出一个字段组件，同时往模型中加入这个字段组件到枚举中；

type FieldKey = string | number | (string | number)[];

// 深度合并的属性
const deepMergePropsList = ['fieldProps', 'formItemProps'];

// 深度合并属性
// props可能有多个，mergeProps也可能有多个，需要合并
const deepMergeProps = (props: Record<string, any>[]) => {
  return props.reduce((acc, curr) => {
    // 其他值直接合并
    const oldAcc = { ...acc };
    acc = { ...oldAcc, ...curr };
    // 深度合并的属性, 深层合并
    deepMergePropsList.forEach((prop) => {
      if (curr[prop]) {
        acc[prop] = { ...oldAcc[prop], ...curr[prop] };
      }
    });
    return acc;
  }, {});
};

/**
 * 字段模型配置选项
 */
interface FieldModelOptions {
  fieldModelName?: string;
  /** 模型上下文数据，可用于存储选项列表等共享数据 */
  context?: Record<string, any>;
}

/**
 * 模型状态管理
 */
export interface ModelState {
  formProps: Record<string, any>;
  /** 所有字段的公共属性 */
  commonProps: Record<string, any>;
  /** 模型上下文数据，可用于存储选项列表等共享数据 */
  context: Record<string, any> & { form?: ProFormInstance<any> };
  /** 设置公共属性的方法 */
  setCommonProps: (props: Record<string, any>) => void;
  /** 设置上下文数据的方法 */
  setContext: (context: Record<string, any>) => void;
  /** 重置mergerEffectProps状态的方法， */
  reset: () => void;
  /** 重置所有状态的方法，包括common */
  resetAll: () => void;
  /** 字段联动属性映射，key为字段ID，value为字段联动属性 */
  effectPropsMap: Record<
    symbol,
    {
      dependencies?: string[];
      effectProps?: (
        formState: Record<string, any>,
        form: ProFormInstance<any>
      ) => Record<string, any>;
    }
  >;
  /** 合并 effectPropsMap 的方法 */
  mergeEffectPropsMap: (
    newEffectPropsMap: Record<
      symbol,
      {
        dependencies?: (string | number | (string | number)[])[];
        effectProps?: (
          formState: Record<string, any>,
          form: ProFormInstance<any>
        ) => Record<string, any>;
      }
    >
  ) => void;
  setFormProps: (props: Record<string, any>) => void;
  resetFormProps: () => void;
}

/**
 * 创建字段模型上下文
 */
export const FieldModelContext = createContext<ModelState | null>(null);

/**
 * 字段模型 Provider 组件属性
 */
export interface FieldModelProviderProps {
  /** 初始proform属性 */
  initialFormProps?: Record<string, any>;
  /** 初始公共属性 */
  initialCommonProps?: Record<string, any>;
  /** 初始上下文数据 */
  initialContext?: Record<string, any>;
  /** 子组件 */
  children: React.ReactNode;
  /** 字段联动属性映射，key为字段ID，value为字段联动属性 */
  effectPropsMap?: Record<
    symbol,
    {
      dependencies?: string[];
      effectProps?: (
        formState: Record<string, any>,
        form: ProFormInstance<any>
      ) => Record<string, any>;
    }
  >;
}

/**
 * 字段模型 Provider 组件
 */
export const FieldModelProvider: React.FC<FieldModelProviderProps> = ({
  initialCommonProps = {},
  initialContext = {},
  children,
  effectPropsMap = {},
  initialFormProps = {},
}) => {
  // proform属性
  const [formProps, setFormPropsState] = useState(initialFormProps);
  // 公共属性状态
  const [commonProps, setCommonPropsState] =
    useState<Record<string, any>>(initialCommonProps);
  // 上下文数据状态
  const [context, setContextState] =
    useState<Record<string, any>>(initialContext);
  // 字段联动属性映射状态
  const [effectPropsMapState, setEffectPropsMapState] = useState<
    Record<
      symbol,
      {
        dependencies?: string[];
        effectProps?: (
          formState: Record<string, any>,
          form: ProFormInstance<any>
        ) => Record<string, any>;
      }
    >
  >(effectPropsMap);

  // 设置公共属性，合并新属性
  const setFormProps = useCallback((props: Record<string, any>) => {
    setFormPropsState((prev) => ({ ...prev, ...props }));
  }, []);

  // 设置公共属性，合并新属性
  const setCommonProps = useCallback((props: Record<string, any>) => {
    setCommonPropsState((prev) => ({ ...prev, ...props }));
  }, []);

  // 设置上下文数据，合并新数据
  const setContext = useCallback((newContext: Record<string, any>) => {
    setContextState((prev) => ({ ...prev, ...newContext }));
  }, []);

  // 重置公共属性和上下文数据为初始值
  const reset = useMemoizedFn(() => {
    setEffectPropsMapState(effectPropsMap);
  });

  const resetAll = useMemoizedFn(() => {
    setCommonPropsState(initialCommonProps);
    setContextState(initialContext);
    setEffectPropsMapState(effectPropsMap);
    setFormProps(initialFormProps);
  });

  const resetFormProps = useMemoizedFn(() => {
    setFormProps(initialFormProps);
  });

  // 合并 effectPropsMap（字段联动属性映射）
  const mergeEffectPropsMap = useMemoizedFn(
    (
      newEffectPropsMap: Record<
        symbol,
        {
          dependencies?: string[];
          effectProps?: (
            formState: Record<string, any>,
            form: ProFormInstance<any>
          ) => Record<string, any>;
        }
      >
    ) => {
      setEffectPropsMapState((prev) => ({ ...prev, ...newEffectPropsMap }));
    }
  );

  // 组合 context value，供 Provider 下所有子组件使用
  const value: ModelState = useMemo(
    () => ({
      commonProps,
      context,
      setCommonProps,
      setContext,
      reset,
      resetAll,
      effectPropsMap: effectPropsMapState,
      mergeEffectPropsMap,
      formProps,
      setFormProps,
      resetFormProps,
    }),
    [
      commonProps,
      context,
      setCommonProps,
      setContext,
      reset,
      effectPropsMapState,
      mergeEffectPropsMap,
      resetAll,
      formProps,
      setFormProps,
      resetFormProps,
    ]
  );

  // 提供上下文
  return (
    <FieldModelContext.Provider value={value}>
      {children}
    </FieldModelContext.Provider>
  );
};

/**
 * 使用字段模型上下文的 Hook
 */
export const useFieldModelContext = (): ModelState => {
  const context = useContext(FieldModelContext);
  if (!context) {
    // 返回默认值，这样即使没有 Provider 也能工作
    return {
      formProps: {},
      commonProps: {},
      context: {},
      setCommonProps: () => {},
      setContext: () => {},
      reset: () => {},
      resetAll: () => {},
      effectPropsMap: {},
      mergeEffectPropsMap: () => {},
      setFormProps: () => {},
    };
  }
  return context;
};

/**
 * 字段显示状态
 */
type DisplayState = 'visible' | 'hidden' | 'none';

/**
 * 基础字段属性，包含依赖项、显示状态和效果属性
 */
type BaseFieldProps<P = any> = {
  /** 依赖的其他字段名称列表，当依赖字段值变化时，当前字段会重新渲染 */
  dependencies?: (string | number | (string | number)[])[];
  /** 字段显示状态 */
  display?: DisplayState;
  /** 根据表单状态动态计算属性的函数 */
  effectProps?: (
    formState: Record<string, any>,
    form: ProFormInstance<any>
  ) => Record<string, any>;
} & Partial<Omit<P, 'name'>>;

/**
 * 字段组件的内部属性
 * @template P - 组件的属性类型
 */
interface FieldComponentProps<P = any> {
  /** 要渲染的组件 */
  Component: React.ComponentType<P>;
  /** 用于生成组件默认属性的 Hook */
  useProps: (
    formState: Record<string, any>,
    form: ProFormInstance<any>
  ) => Partial<P>;
  /** 字段的唯一标识 */
  fieldName: FieldKey;
  /** 从模型状态中获取的公共属性 */
  commonProps: Partial<P>;
  /** 动态计算属性的函数 */
  effectProps?: (
    formState: Record<string, any>,
    form: ProFormInstance<any>
  ) => Partial<P & { display?: DisplayState }>;
  /** 当前表单状态 */
  formState: any;
  /** 从父组件传入的其他属性 */
  restProps: Partial<P & { display?: DisplayState }>;
  /** 字段模型 */
  model: FieldModel;
  /** 字段ID */
  fieldId: symbol;
  /** 表单实例 */
  form: ProFormInstance<any>;
}

/**
 * 包装后的字段组件接口
 * @template P - 组件的属性类型
 */
export interface FieldItemComponent<
  P extends Record<string, any> = Record<string, any>
> extends React.MemoExoticComponent<React.FC<BaseFieldProps<P>>> {
  /** 原始字段名称 */
  originalName: FieldKey;
  /** 当前字段名称 */
  name: FieldKey;
  /** 字段唯一标识 */
  ID: symbol;
  /** 字段模型实例 */
  model: FieldModel;
  /** 合并属性的方法 */
  mergeProps: (
    props: Partial<P> & BaseFieldProps<P>,
    fieldName?: FieldKey
  ) => void;
}

const useMergeProps = (props: FieldComponentProps<any>) => {
  const {
    useProps,
    fieldName,
    commonProps,
    effectProps,
    formState,
    form,
    restProps,
    fieldId,
    model,
  } = props;

  const { effectPropsMap } = useFieldModelContext();

  const defaultProps = useProps(formState, form);

  // 从 effectPropsMap 中获取字段ID对应的联动属性
  const contextEffectProps = effectPropsMap[fieldId];

  // 计算 effectProps 结果
  const effectPropsResult = useMemo(() => {
    return effectProps ? effectProps(formState, form) : {};
  }, [effectProps, formState, form]);

  // 计算 contextEffectProps 结果
  const contextEffectPropsResult = useMemo(() => {
    return contextEffectProps?.effectProps
      ? contextEffectProps.effectProps(formState, form)
      : {};
  }, [contextEffectProps?.effectProps, formState, form]);

  // 获取 mergeProps 中存储的属性
  const mergeProps = model.getFieldMergeProps(fieldId) || {};

  // 五层属性合并：useProps -> mergeProps -> common -> FieldItem.props -> effectProps -> contextEffectProps
  const resultProps = deepMergeProps([
    {
      name: fieldName,
    },
    defaultProps, // 第一层：useProps 返回的默认属性
    mergeProps, // 第二层：mergeProps 设置的属性
    commonProps, // 第三层：模型公共属性
    restProps, // 第四层：FieldItem.props 传入的属性
    effectPropsResult, // 第五层：组件级别的动态计算属性
    contextEffectPropsResult, // 最后：上下文级别的动态计算属性,通过mergeEffectPropsMap 设置的属性（优先级最高）
  ]);

  // 为了方便调试，在resultProps中添加一个__debug__属性，值为一个对象，分别存储
  resultProps.__filedModelDebug__ = [
    {
      level: 0,
      key: 'name',
      description: '字段名称, createFieldItem 传入的 fieldName',
      props: {
        name: fieldName,
      },
    },
    {
      level: 1,
      key: 'defaultProps',
      description: '创建 FieldItem 时，设置的默认属性',
      props: defaultProps,
    },
    {
      level: 2,
      key: 'mergeProps',
      description: '通过 mergeProps 设置的属性',
      props: mergeProps,
    },
    {
      level: 3,
      key: 'commonProps',
      description:
        '通过 FieldModelProvider 设置的公共属性，可以通过 setCommonProps 设置',
      props: commonProps,
    },
    {
      level: 4,
      key: 'FieldItem.props',
      description: '通过实例层的props传入的属性',
      props: restProps,
    },
    {
      level: 5,
      key: 'FieldItem.effectProps',
      description: '通过实例层的effectProps和dependencies 设置的属性',
      props: effectPropsResult,
    },
    {
      level: 6,
      key: 'mergeEffectPropsMap',
      description: '通过上下文中组件属性，通过mergeEffectPropsMap 设置的属性',
      props: contextEffectPropsResult,
    },
  ].filter((item) => item.props && Object.keys(item.props).length > 0);
  return resultProps;
};

// 使用 memo 优化字段组件，只在 props 真正变化时重渲染
const FieldComponentInner = <P extends Record<string, any>>(
  props: FieldComponentProps<P>
) => {
  const { Component } = props;

  // 使用 useMergeProps 统一处理属性合并逻辑
  // 这里传入一个空的 form 对象，因为 FieldComponentInner 本身没有 form 实例
  const mergedProps = useMergeProps(props);

  // 获取display状态，默认为visible
  const displayState = (mergedProps as any).display || 'visible';

  // 如果显示状态为 'none'，直接返回 null
  if (displayState === 'none') {
    return null;
  }

  // 从mergedProps中移除display属性，避免传递给底层组件
  const { display, ...componentProps } = mergedProps as any;

  // 如果readonly为true，则required为false
  if ((componentProps as any).readonly) {
    (componentProps as any).required = false;
  }
  const component = <Component {...(componentProps as P)} />;

  // 如果显示状态为 'hidden'，用 div 包装并隐藏
  if (displayState === 'hidden') {
    return <div style={{ display: 'none' }}>{component}</div>;
  }

  // 正常显示
  return component;
};

// 创建 memo 包装的组件，使用 lodash 的 isEqual 进行深度比较
const FieldComponent = memo(FieldComponentInner, (prevProps, nextProps) => {
  // 自定义比较函数，使用 isEqual 进行深度比较
  return (
    prevProps.fieldName === nextProps.fieldName &&
    prevProps.Component === nextProps.Component &&
    prevProps.fieldId === nextProps.fieldId &&
    isEqual(prevProps.formState, nextProps.formState) &&
    isEqual(prevProps.commonProps, nextProps.commonProps) &&
    isEqual(prevProps.restProps, nextProps.restProps)
  );
}) as typeof FieldComponentInner;

/**
 * 字段模型类，用于创建和管理表单字段组件
 */
class FieldModel {
  /** 存储已创建的字段组件 */
  private fields: Map<symbol, FieldItemComponent<any>> = new Map();

  private fieldNameMap: Map<symbol, FieldKey> = new Map();

  private fieldMergePropsMap: Map<symbol, any> = new Map();

  private fieldModelName: string; // 字段模型名称，用于字段组件命名空间

  setFieldNameById(fieldId: symbol, fieldName: FieldKey) {
    // 判断原来filedName和originalName如果不同，且新的fieldName和原来fieldName不同则不允许修改，原因是一个field不能有两个name
    const FieldItem = this.fields.get(fieldId);
    if (!FieldItem) return;

    const originalName = FieldItem.originalName;
    const oldFieldName = this.fieldNameMap.get(fieldId);
    // 如果相同则不设置
    if (isEqual(fieldName, oldFieldName)) {
      return;
    }
    this.fieldNameMap.set(fieldId, fieldName);
  }

  /**
   * 获取字段的 mergeProps 属性
   * @param fieldId - 字段ID
   * @returns mergeProps 中存储的属性
   */
  getFieldMergeProps(fieldId: symbol): Record<string, any> {
    return this.fieldMergePropsMap.get(fieldId) || {};
  }

  /**
   * 设置字段的 mergeProps 属性
   * @param fieldId - 字段ID
   * @param props - 要设置的属性
   */
  setFieldMergeProps(fieldId: symbol, props: Record<string, any>) {
    this.fieldMergePropsMap.set(fieldId, props);
  }

  constructor(options: FieldModelOptions = {}) {
    // 不再需要创建 zustand store，context 现在通过 Provider 提供
    this.fieldModelName = options.fieldModelName || '';
  }

  getFieldKey(field: FieldItemComponent<any>): FieldKey {
    return this.fieldNameMap.get(field.ID);
  }

  getFieldValue(field: FieldItemComponent<any>, formState: any) {
    return get(formState, this.getFieldKey(field));
  }

  /**
   * 创建字段组件
   * @template P - 组件的属性类型
   * @param fieldName - 字段名称，直接作为表单字段的 name
   * @param Component - 要包装的表单组件
   * @param useProps - 用于生成组件默认属性的 Hook
   * @param defaultDependencies - 默认依赖的字段列表
   * @returns 包装后的字段组件
   */
  createFieldItem<P extends Record<string, any> = Record<string, any>>(
    fieldName: FieldKey,
    Component: React.ComponentType<P>,
    useProps: (formState: any, form: ProFormInstance<any>) => Partial<P>,
    defaultDependencies?: FieldKey[]
  ): FieldItemComponent<P> {
    const fieldId = Symbol(
      Array.isArray(fieldName) ? fieldName.join('.') : String(fieldName)
    );
    this.fieldNameMap.set(fieldId, fieldName);

    // 使用 memo 缓存 FieldItem 组件
    const FieldItem = memo<BaseFieldProps<P>>(
      (props) => {
        const { commonProps, context, effectPropsMap } = useFieldModelContext();
        const {
          effectProps,
          dependencies: itemDependencies,
          ...restProps
        } = props;
        const mergeProps = this.getFieldMergeProps(fieldId);
        const {
          dependencies: mergeDependencies,
          effectProps: mergeEffectProps,
          ...mergeRestProps
        } = mergeProps;

        // 从 effectPropsMap 中获取字段ID对应的联动属性
        const contextEffectProps = effectPropsMap[fieldId];

        // 从合并后的属性中获取 dependencies
        const finalDependencies = [
          ...(defaultDependencies || []),
          ...(itemDependencies || []),
          ...(mergeDependencies || []),
          ...(contextEffectProps?.dependencies || []),
        ];

        const finalEffectProps = useCallback(
          (formState: any, form: ProFormInstance<any>) => {
            const effectPropsResult = effectProps
              ? effectProps(formState, form)
              : {};
            const mergeEffectPropsResult = mergeEffectProps
              ? mergeEffectProps(formState, form)
              : {};

            return {
              ...effectPropsResult,
              ...mergeEffectPropsResult,
            };
          },
          [effectProps, mergeEffectProps, contextEffectProps]
        );

        const finalRestProps = useMemo(() => {
          return {
            ...(restProps || {}),
            ...(mergeRestProps || {}),
          };
        }, [restProps, mergeRestProps]);

        // 使用 useMemoizedFn 缓存渲染函数
        const renderFieldComponent = useMemoizedFn(
          (formState: any, form: ProFormInstance<any>) => {
            const finalFiledName = this.fieldNameMap.get(fieldId);
            return (
              <FieldComponent<P>
                Component={Component}
                useProps={useProps}
                fieldName={finalFiledName}
                commonProps={commonProps as Partial<P>}
                effectProps={finalEffectProps}
                formState={formState}
                form={form}
                restProps={finalRestProps as any}
                fieldId={fieldId}
                model={this}
              />
            );
          }
        );

        // 如果有依赖项，使用 ProForm.Item 的 noStyle 和 dependencies
        if (finalDependencies && finalDependencies.length > 0) {
          return (
            <ProFormDependency name={finalDependencies}>
              {(value, form: ProFormInstance<any>) => {
                // const formState = form.getFieldsValue();
                return renderFieldComponent(value, form);
              }}
            </ProFormDependency>
          );
        }

        // 如果没有依赖项，直接渲染组件
        return renderFieldComponent({}, {});
      },
      (prevProps, nextProps) => {
        // 自定义比较函数，只在关键属性变化时重渲染
        return false;
      }
    );

    // 为 FieldItem 添加必要的属性，创建 FieldItemComponent
    const FieldItemWithProps: FieldItemComponent<P> = Object.assign(FieldItem, {
      originalName: fieldName,
      name: fieldName,
      ID: fieldId,
      model: this,
      mergeProps: (
        props: Partial<P> & BaseFieldProps<P>,
        fieldName?: FieldKey
      ) => {
        // 如果提供了 fieldName，则更新字段名称
        if (fieldName !== undefined) {
          this.setFieldNameById(fieldId, fieldName);
        }
        // 设置 mergeProps 属性
        this.setFieldMergeProps(fieldId, props);
      },
    });

    // 设置react调试中显示的组件名称
    FieldItemWithProps.displayName =
      this.fieldModelName +
      '.' +
      (Array.isArray(fieldName) ? fieldName.join('.') : String(fieldName));

    // 存储字段组件
    this.fields.set(fieldId, FieldItemWithProps);

    return FieldItemWithProps;
  }

  getFieldsList(): FieldItemComponent<any>[] {
    return Array.from(this.fields.values());
  }

  /**
   * 获取用于操作模型状态的 Hook（为了兼容性保留）
   * 也可以直接使用 FieldModelProvider 和 useFieldModelContext
   */
  get useModelState() {
    return useFieldModelContext;
  }
}

export default FieldModel;
